/**
 * RBAC-6.1 — one `my_account_modules()` read per account per page, shared by every hook that asks.
 *
 * Keyed by the ACTIVE tenant (TenantContext), not `userInfo.tenant_id`, which is a superadmin's home
 * account and does not change when they switch (the W1R P13 trap `useCommissionCostingEnabled` hit).
 * Registered with scopeCaches so an account switch, a sign-out and RBAC-4.3's revalidation clear it.
 *
 * A failed read resolves to `null`, which every caller treats as "all off".
 */

import type { AccountModuleStatus } from '@/lib/accountFeatures';
import { registerScopeCache } from '@/lib/scopeCaches';

type Entry = { features: AccountModuleStatus[] | null; resolved: boolean; inflight?: Promise<AccountModuleStatus[] | null> };

const cache = new Map<string, Entry>();

export function accountFeaturesCacheKey(tenantId: string | null | undefined): string {
  return tenantId && tenantId.length > 0 ? tenantId : '__pending__';
}

export function peekAccountFeatures(tenantId: string | null | undefined): Entry | null {
  return cache.get(accountFeaturesCacheKey(tenantId)) ?? null;
}

export function getOrStartAccountFeaturesFetch(
  tenantId: string | null | undefined,
  fetchFeatures: () => Promise<AccountModuleStatus[]>,
): Promise<AccountModuleStatus[] | null> {
  const key = accountFeaturesCacheKey(tenantId);
  const existing = cache.get(key);
  if (existing?.resolved) return Promise.resolve(existing.features);
  if (existing?.inflight) return existing.inflight;

  const inflight = fetchFeatures()
    .then((features) => features)
    .catch(() => null)
    .then((features) => {
      // Only settle the entry this fetch created: a clear in the meantime means the answer is stale.
      if (cache.get(key)?.inflight === inflight) cache.set(key, { features, resolved: true });
      return features;
    });
  cache.set(key, { features: null, resolved: false, inflight });
  return inflight;
}

export function clearAccountFeaturesCache(): void {
  cache.clear();
}

/** Fired after a superadmin switches a feature, so mounted hooks re-read rather than keep the old answer. */
export const ACCOUNT_FEATURES_CHANGED = 'joules:account-features-changed';

export function notifyAccountFeaturesChanged(): void {
  cache.clear();
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(ACCOUNT_FEATURES_CHANGED));
}

registerScopeCache('accountFeaturesCache', clearAccountFeaturesCache);
